/**
 * Options resolution – single place for all options-related logic.
 * Used by:
 * - field-config/process-static-content.js (check type field configs; optional template `sources` on resolveOptions)
 * - field-config/process-dynamic-content.js (dynamic options by form values)
 * - candidate_application/process-static-content.js (candidate fields – resolveOptionsFromSource only)
 *
 * Order: constants → low-level resolution → filters → config → static resolution → strip → form-based → dynamic resolution.
 *
 * After resolution:
 * - processField (process-static-content) sets field.options = resolved array and deletes field.exclude / field.include
 *   so downstream (toQuestion, processDynamicField) does not reprocess.
 * - Resolved option lists are plain arrays of { name, value }; source/exclude/include/conditions are not kept on the field.
 * - Option-level conditions are evaluated in resolveOptionsWithFormValues; the `conditions` key is stripped from
 *   each option before pushing (optionWithoutConditions) so the final list has no condition keys.
 */
import { countries } from '../constants/countries';
import adminDivisions from '../constants/country-admin-divisions';
import { isoCurrencies } from '../constants/iso-currencies';
import evaluateFieldConditions from './evaluate-field-conditions';
import { readValueByName } from './field-paths';

// ============================================================================
// Constants
// ============================================================================

const SOURCES = {
  adminDivisions,
  countries,
  isoCurrencies,
};

// Base type configurations for country/state/region/currency types (used by field config, not candidate_application)
export const BASE_TYPE_CONFIG = {
  country: { source: 'countries', valueKey: 'value' },
  country_alpha2: { source: 'countries', valueKey: 'alpha2' },
  country_alpha3: { source: 'countries', valueKey: 'alpha3' },
  au_state: { source: 'adminDivisions.AU_STATES', valueKey: 'value' },
  us_state: { source: 'adminDivisions.US_STATES', valueKey: 'value' },
  nz_region: { source: 'adminDivisions.NZ_REGIONS', valueKey: 'value' },
  currency: { source: 'isoCurrencies', valueKey: 'value' },
};

// ============================================================================
// Low-level: resolve from path (used by field config and candidate_application)
// ============================================================================

/**
 * Resolve options from a source path (e.g. "countries", "adminDivisions.AU_STATES").
 * Used by field config and candidate_application/process-static-content.js.
 */
export const resolveOptionsFromSource = (
  path,
  { nameKey = 'name', valueKey = 'value', exclude = [] } = {},
) => {
  if (!path || typeof path !== 'string') return [];

  const [sourceName, ...nestedKeys] = path.split('.');
  const sourceObject = SOURCES?.[sourceName];
  if (!sourceObject) return [];

  let resolved = sourceObject;
  for (const key of nestedKeys) {
    if (resolved == null) return [];
    resolved = resolved[key];
  }

  resolved = resolved ?? SOURCES?.[path];
  if (!resolved || typeof resolved !== 'object') return [];

  const rawOptions = Array.isArray(resolved) ? resolved : Object.values(resolved);

  return rawOptions
    .filter(opt => !exclude.includes(opt[valueKey]))
    .map(opt => ({
      name: opt[nameKey] ?? opt[valueKey],
      value: opt[valueKey],
    }));
};

/**
 * Rows from FieldConfigRenderer `sources` (not in SOURCES): allow `id` when `valueKey` is absent.
 * Same output shape as resolveOptionsFromSource mapping.
 */
const mapContextSourceRowsToOptions = (raw, nameKey, valueKey) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const v = item[valueKey] ?? item.id;
      if (v == null && v !== 0) return null;
      const nm = item[nameKey];
      return {
        name: nm != null && nm !== '' ? nm : String(v),
        value: v,
      };
    })
    .filter(Boolean);
};

// ============================================================================
// Filter helpers
// ============================================================================

const toFilterArray = v => (v == null ? [] : Array.isArray(v) ? v : [v]);

export const applyIncludeExcludeFilter = (options, include = [], exclude = []) => {
  if (include.length === 0 && exclude.length === 0) return options;
  return options.filter(opt => {
    const optValue = opt?.value ?? opt;
    if (include.length > 0 && !include.includes(optValue)) return false;
    if (exclude.length > 0 && exclude.includes(optValue)) return false;
    return true;
  });
};

// ============================================================================
// Config and type checks (field config source types)
// ============================================================================

// Exclude/include are read only from the options object (never from field root).
export const getOptionsConfigForSourceType = (field, existingOptions = null) => {
  const opts = existingOptions ?? field?.options ?? {};
  const isSourceObject = opts && typeof opts === 'object' && !Array.isArray(opts) && opts.source;
  if (isSourceObject) {
    return {
      ...opts,
      exclude: toFilterArray(opts.exclude),
      include: toFilterArray(opts.include),
    };
  }
  // For country/admin-division types: options may be { exclude, include } without source.
  return {
    exclude: toFilterArray(opts.exclude),
    include: toFilterArray(opts.include),
  };
};

export const hasSourceType = type => {
  if (!type) return false;
  const baseType = type.startsWith('multi_') ? type.substring(6) : type;
  return baseType in BASE_TYPE_CONFIG;
};

// ============================================================================
// Static resolution (type + config → array)
// ============================================================================

/**
 * Resolve options for source-based types (countries, states).
 * If config has source, use it; otherwise use BASE_TYPE_CONFIG from type.
 * Returns array of { name, value }; apply exclude/include via applyIncludeExcludeFilter.
 *
 * When `templateSources` is passed (FieldConfigRenderer `sources`) and built-in resolution is empty,
 * reads `existingOptions.source` from that object (e.g. company_teams rows).
 */
export const resolveOptions = (type, existingOptions, templateSources) => {
  if (!type) return existingOptions;

  if (existingOptions && typeof existingOptions === 'object' && existingOptions.source) {
    const exclude = toFilterArray(existingOptions.exclude);
    const include = toFilterArray(existingOptions.include);
    const valueKey = existingOptions.valueKey || existingOptions.value_key || 'value';
    const nameKey = existingOptions.nameKey || existingOptions.name_key || 'name';
    const options = resolveOptionsFromSource(existingOptions.source, {
      valueKey,
      exclude: [],
    });
    let result = applyIncludeExcludeFilter(options, include, exclude);

    if (
      templateSources &&
      typeof templateSources === 'object' &&
      Array.isArray(result) &&
      result.length === 0 &&
      typeof existingOptions.source === 'string' &&
      existingOptions.source.length > 0 &&
      !hasFormBasedOptions(existingOptions)
    ) {
      const raw = readValueByName(templateSources, existingOptions.source);
      if (Array.isArray(raw)) {
        const mapped = mapContextSourceRowsToOptions(raw, nameKey, valueKey);
        result = applyIncludeExcludeFilter(mapped, include, exclude);
      }
    }

    return result;
  }

  const baseType = type.startsWith('multi_') ? type.substring(6) : type;
  const config = BASE_TYPE_CONFIG[baseType];
  if (!config || !config.source) return existingOptions;

  const exclude = toFilterArray(existingOptions?.exclude);
  const include = toFilterArray(existingOptions?.include);
  const options = resolveOptionsFromSource(config.source, {
    valueKey: config.valueKey,
    exclude: [],
  });
  return applyIncludeExcludeFilter(options, include, exclude);
};

export const wrapMultipleChoiceOptions = (options, camelField) => {
  if (options && !Array.isArray(options) && Array.isArray(options.options)) return options;
  return {
    multipleSelection: camelField.multipleSelection || false,
    otherOption: camelField.otherOption || false,
    options: Array.isArray(options) ? options : [],
  };
};

// ============================================================================
// Strip config after resolution (avoid reprocessing)
// ============================================================================

/**
 * Remove exclude/include from field after options are resolved.
 * Call after setting field.options = resolvedArray so downstream code doesn't re-apply filters.
 */
export const stripOptionsConfigFromField = field => {
  if (!field || typeof field !== 'object') return field;
  const next = { ...field };
  delete next.exclude;
  delete next.include;
  return next;
};

// ============================================================================
// Form-based options (dynamic resolution)
// ============================================================================

const STATIC_OPTION_SOURCES = ['countries', 'adminDivisions', 'isoCurrencies', 'option_sets'];

const isStaticOptionSource = source => {
  if (!source || typeof source !== 'string') return false;
  return STATIC_OPTION_SOURCES.some(prefix => source === prefix || source.startsWith(`${prefix}.`));
};

const resolveOptionSourceItem = (item, formValues, optionsLookup, fieldContext) => {
  if (!item?.source || item.value != null) return [];
  const source = item.source;
  const include = resolveFilterValues(item.include, formValues, fieldContext);
  const exclude = resolveFilterValues(item.exclude, formValues, fieldContext);
  const mergedSources = { ...(fieldContext?.sources ?? {}), ...formValues };

  let itemOptions = [];
  if (isStaticOptionSource(source)) {
    itemOptions = resolveOptions(null, { source }, mergedSources);
  }
  if (itemOptions.length === 0) {
    itemOptions = resolveFormFieldSource(source, mergedSources, optionsLookup, fieldContext);
  }
  if (itemOptions.length === 0 && isStaticOptionSource(source) && include.length > 0) {
    itemOptions = convertFormValueToOptions(include, source, optionsLookup);
  }
  return applyIncludeExcludeFilter(itemOptions, include, exclude);
};

const appendResolvedOptionItem = (allOptions, item, formValues) => {
  if (!item || typeof item !== 'object') return;
  if (item.source && item.value == null) return;

  if (item.conditions) {
    const shouldShow = evaluateFieldConditions(item.conditions, formValues);
    if (!shouldShow) return;
    const { conditions, ...optionWithoutConditions } = item;
    item = optionWithoutConditions;
  }

  if (item.value != null) {
    allOptions.push({
      name: item.name != null && item.name !== '' ? item.name : String(item.value),
      value: item.value,
    });
    return;
  }

  allOptions.push(item);
};

export const hasFormBasedOptions = options => {
  if (!options || typeof options !== 'object') return false;
  if (Array.isArray(options)) {
    return options.some(
      item =>
        item &&
        typeof item === 'object' &&
        item.source &&
        item.value == null &&
        typeof item.source === 'string',
    );
  }
  if (options.source && typeof options.source === 'string' && !options.value) {
    if (options.source === 'company_teams') return false;
    if (isStaticOptionSource(options.source)) return false;
    return true;
  }
  if (options.options && Array.isArray(options.options)) {
    return options.options.some(
      item =>
        item &&
        typeof item === 'object' &&
        item.source &&
        !item.value &&
        typeof item.source === 'string' &&
        !isStaticOptionSource(item.source),
    );
  }
  return false;
};

export const getOptionCountBehavior = options => {
  if (!Array.isArray(options)) return 'show';
  if (options.length === 0) return 'hide';
  if (options.length === 1) return 'auto';
  return 'show';
};

const extractOptionsFromFormValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.options)) {
    return value.options;
  }
  return value;
};

export const isOptionSetsLookup = optionSets => {
  if (!optionSets || typeof optionSets !== 'object' || Array.isArray(optionSets)) {
    return false;
  }
  return Object.values(optionSets).every(
    entries => entries && typeof entries === 'object' && !Array.isArray(entries),
  );
};

const isSlugStringArray = values => {
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.every(
    item =>
      (typeof item === 'string' || typeof item === 'number') &&
      !(item && typeof item === 'object' && item.value != null),
  );
};

const resolveStoredOptionSetItem = (slug, optionSetsLookup, setCodes) => {
  for (const code of setCodes) {
    const set = optionSetsLookup?.[code];
    if (!set || typeof set !== 'object') continue;
    const row = set[slug] ?? Object.values(set).find(entry => entry?.value === slug);
    if (!row) continue;
    return {
      name: row.name != null && row.name !== '' ? row.name : slug,
      value: slug,
    };
  }

  return null;
};

export const expandSlugArrayToOptions = (slugs, optionSetsLookup, setCodes = []) => {
  if (!Array.isArray(slugs) || slugs.length === 0) return [];
  const codes =
    setCodes.length > 0 ? setCodes : Object.keys(optionSetsLookup ?? {}).filter(Boolean);
  const options = [];
  const seen = new Set();
  for (const slug of slugs) {
    if (typeof slug !== 'string' && typeof slug !== 'number') continue;
    const key = String(slug);
    if (seen.has(key)) continue;
    seen.add(key);
    options.push(
      resolveStoredOptionSetItem(key, optionSetsLookup, codes) ?? {
        name: key,
        value: key,
      },
    );
  }
  return options;
};

export const expandSlugArraysInObject = (object, optionSetsLookup, setCodes = []) => {
  if (!object || typeof object !== 'object') return object;
  const codes =
    setCodes.length > 0 ? setCodes : Object.keys(optionSetsLookup ?? {}).filter(Boolean);
  const expanded = { ...object };
  for (const [fieldCode, raw] of Object.entries(object)) {
    if (!isSlugStringArray(raw)) continue;
    expanded[fieldCode] = expandSlugArrayToOptions(raw, optionSetsLookup, codes);
  }
  return expanded;
};

const convertFormValueToOptions = (value, fieldName, optionsLookup = {}) => {
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'string') return [{ name: value, value }];
  if (Array.isArray(value)) {
    const fieldLookupOptions = optionsLookup[fieldName] || [];
    const resolvedOptions = [];
    for (const valItem of value) {
      if (typeof valItem === 'string' || typeof valItem === 'number') {
        const slug = String(valItem);
        if (!resolvedOptions.some(opt => opt.value === slug)) {
          const lookupOption = fieldLookupOptions.find(opt => opt.value === slug);
          resolvedOptions.push({
            name: lookupOption?.name || slug,
            value: slug,
          });
        }
      } else if (valItem && typeof valItem === 'object' && valItem.value) {
        if (!resolvedOptions.some(opt => opt.value === valItem.value)) {
          resolvedOptions.push({
            name: valItem.name || valItem.value,
            value: valItem.value,
          });
        }
      }
    }
    return resolvedOptions;
  }
  return [];
};

const resolveFormFieldSource = (fieldName, formValues, optionsLookup = {}, fieldContext = {}) => {
  const mergedSources = { ...(fieldContext?.sources ?? {}), ...formValues };
  let value = mergedSources[fieldName] ?? readValueByName(mergedSources, fieldName);
  value = extractOptionsFromFormValue(value);
  return convertFormValueToOptions(value, fieldName, optionsLookup);
};

export const resolveFilterValues = (filter, formValues = {}, fieldContext = {}) => {
  if (Array.isArray(filter)) return filter;
  if (typeof filter !== 'string') return [];
  if (filter.includes('.')) {
    const mergedSources = { ...(fieldContext?.sources ?? {}), ...formValues };
    const value = readValueByName(mergedSources, filter);
    if (value === undefined || value === null) return [];
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0].value) {
        return value.map(item => item.value);
      }
      return value;
    }
    return [value];
  }
  const value = formValues[filter];
  if (value !== undefined && value !== null) {
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0].value) {
        return value.map(item => item.value);
      }
      return value;
    }
    return [value];
  }
  return [];
};

/**
 * Resolve options that depend on form values (form field references or option-level conditions).
 * optionsConfig: { source, include, exclude } (form field) or { options: [...], dynamic_dependency_codes }
 * Returns array of { name, value } (option-level conditions stripped from each option).
 */
const resolveCompoundOptions = (items, formValues, optionsLookup, fieldContext) => {
  const allOptions = [];
  for (const item of items) {
    if (item && typeof item === 'object' && item.source && item.value == null) {
      allOptions.push(...resolveOptionSourceItem(item, formValues, optionsLookup, fieldContext));
      continue;
    }
    appendResolvedOptionItem(allOptions, item, formValues);
  }
  return allOptions;
};

export const resolveOptionsWithFormValues = (
  optionsConfig,
  formValues = {},
  optionsLookup = {},
  fieldContext = {},
) => {
  if (!optionsConfig) return optionsConfig;
  if (Array.isArray(optionsConfig)) {
    return resolveCompoundOptions(optionsConfig, formValues, optionsLookup, fieldContext);
  }
  if (typeof optionsConfig !== 'object') return optionsConfig;

  if (
    optionsConfig.source &&
    typeof optionsConfig.source === 'string' &&
    optionsConfig.value == null
  ) {
    return resolveOptionSourceItem(optionsConfig, formValues, optionsLookup, fieldContext);
  }

  if (optionsConfig.options && Array.isArray(optionsConfig.options)) {
    return resolveCompoundOptions(optionsConfig.options, formValues, optionsLookup, fieldContext);
  }

  return optionsConfig;
};
